import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { revisionText } from '../documents/library.js';
import type { LibraryRevision } from '../documents/types.js';
import classes from './Forms.module.css';
import { type NewRecordValues, newRecordSchema } from './record-forms.js';
import { formErrorText } from './status.js';

export interface NewRecordInput {
  /** Kâğıt istisnasında eski revizyon; yürürlükteki revizyonda verilmez. */
  revisionId?: string;
  paperRevisionReason?: string;
  title: string | null;
}

/**
 * New record (ADR 0042 §3, §21): taranmış ya da fotoğraflanmış kâğıt kayda alınır; şablon dosyası
 * istemez, her zaman açıktır. Kâğıt, yürürlükten kalkmış ya da geri çekilmiş bir revizyonun basılı
 * kopyasıysa o revizyon seçilir ve gerekçe zorunludur [sorular #228]; kayıt işaretlenir.
 * Taslak açılınca kayıt numarası ayrılır; taramalar kayıt sayfasında eklenir.
 */
export function NewRecordDialog({
  opened,
  formName,
  current,
  paper,
  onClose,
  onCreate,
}: {
  opened: boolean;
  /** "Form 05 - Fire Drill". */
  formName: string;
  current: { id: string; revisionNo: string } | null;
  /** Kâğıt istisnasında seçilebilecek revizyonlar. */
  paper: readonly Pick<LibraryRevision, 'id' | 'revisionNo' | 'state'>[];
  onClose: () => void;
  onCreate: (input: NewRecordInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<unknown>(null);
  const defaults: NewRecordValues = {
    revisionId: current?.id ?? paper[0]?.id ?? '',
    current: current !== null,
    title: '',
    paperReason: '',
  };
  const form = useForm<NewRecordValues>({
    resolver: zodResolver(newRecordSchema),
    defaultValues: defaults,
  });
  useEffect(() => {
    if (opened) {
      form.reset({
        revisionId: current?.id ?? paper[0]?.id ?? '',
        current: current !== null,
        title: '',
        paperReason: '',
      });
      setError(null);
    }
  }, [opened, current, paper, form]);
  const isCurrent = form.watch('current');
  const fieldError = (name: keyof NewRecordValues) => {
    const message = form.formState.errors[name]?.message;
    return message ? t(`forms.errors.${message}`) : undefined;
  };
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onCreate({
        ...(values.current
          ? {}
          : { revisionId: values.revisionId, paperRevisionReason: values.paperReason }),
        title: values.title === '' ? null : values.title,
      });
    } catch (caught) {
      setError(caught);
    }
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('forms.newRecord.title')}
      closeButtonProps={{ 'aria-label': t('forms.dialog.back') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            {t('forms.newRecord.intro', { form: formName })}
          </Alert>
          <Controller
            control={form.control}
            name="current"
            render={({ field }) => (
              <Radio.Group
                label={t('forms.newRecord.paperOf')}
                value={field.value ? 'current' : 'paper'}
                onChange={(value) => {
                  field.onChange(value === 'current');
                  form.setValue(
                    'revisionId',
                    value === 'current' ? (current?.id ?? '') : (paper[0]?.id ?? ''),
                  );
                }}
              >
                <Stack gap={6} mt={6}>
                  <Radio
                    value="current"
                    disabled={!current}
                    label={
                      current
                        ? t('forms.newRecord.current', {
                            revision: revisionText(current.revisionNo),
                          })
                        : t('forms.newRecord.noCurrent')
                    }
                  />
                  <Radio
                    value="paper"
                    disabled={paper.length === 0}
                    label={t('forms.newRecord.paper')}
                  />
                </Stack>
              </Radio.Group>
            )}
          />
          {!isCurrent ? (
            <>
              <Controller
                control={form.control}
                name="revisionId"
                render={({ field }) => (
                  <Select
                    label={t('forms.newRecord.revision')}
                    data={paper.map((r) => ({
                      value: r.id,
                      label: `${revisionText(r.revisionNo)} · ${t(`documents.states.${r.state}`)}`,
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value ?? '')}
                    allowDeselect={false}
                    required
                  />
                )}
              />
              <Textarea
                label={t('forms.newRecord.paperReason')}
                description={t('forms.newRecord.paperHint')}
                required
                autosize
                minRows={2}
                maxLength={2000}
                {...form.register('paperReason')}
                error={fieldError('paperReason')}
              />
            </>
          ) : null}
          <TextInput
            label={t('forms.record.title')}
            description={t('forms.newRecord.titleHint')}
            maxLength={400}
            {...form.register('title')}
            error={fieldError('title')}
          />
          {error ? (
            <Alert color="red" role="alert">
              {formErrorText(t, error)}
            </Alert>
          ) : null}
          <p className={classes.muted}>{t('forms.newRecord.number')}</p>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('forms.dialog.back')}
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {t('forms.newRecord.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
